import { useNavigate } from "@tanstack/react-router";
import { ClipboardCheck, Plus, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { ProjectTeamDialog } from "@/components/projects/ProjectTeamDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { AuditListItem } from "@/hooks/use-audits";
import { useAudits } from "@/hooks/use-audits";
import type { Project } from "@/hooks/use-projects";
import { useAllProjects } from "@/hooks/use-projects";
import { ACTIVE_AUDIT_STATUSES } from "@/lib/audit-status";
import { useSession } from "@/lib/auth-client";
import { CreateAuditDialog } from "./CreateAuditDialog";

const COLUMNS: { key: string; label: string }[] = [
  { key: "assigned", label: "Assigned" },
  { key: "guidance_requested", label: "Guidance Requested" },
  { key: "in_progress", label: "In Progress" },
  { key: "complete", label: "Complete" },
  { key: "blocked", label: "Blocked" },
];

const COLUMN_COLORS: Record<string, string> = {
  assigned: "border-t-slate-400",
  guidance_requested: "border-t-purple-500",
  in_progress: "border-t-blue-500",
  complete: "border-t-emerald-500",
  blocked: "border-t-red-500",
};

const BADGE_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  assigned: "outline",
  guidance_requested: "secondary",
  in_progress: "secondary",
  complete: "default",
  blocked: "destructive",
};

function AuditCard({
  audit,
  project,
  onShowTeam,
}: {
  audit: AuditListItem;
  project: Project | undefined;
  onShowTeam: (project: Project) => void;
}) {
  const navigate = useNavigate();
  const pct = audit.totalItems > 0 ? Math.round((audit.checkedItems / audit.totalItems) * 100) : 0;

  return (
    <div className="relative bg-card border border-border rounded-lg shadow-sm hover:shadow-md hover:border-primary/30 transition-all">
      <button
        type="button"
        className="w-full text-left p-3 space-y-2 cursor-pointer"
        onClick={() =>
          navigate({ to: "/dashboard/audits/$auditId", params: { auditId: audit.id } })
        }
      >
        <div className="font-medium text-sm leading-tight pr-6">{audit.projectName}</div>
        {audit.accountName && (
          <div className="text-xs text-muted-foreground">{audit.accountName}</div>
        )}
        <div className="text-xs text-muted-foreground">{audit.templateName}</div>

        {audit.assignedToName && (
          <div className="flex items-center gap-1.5">
            <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary">
              {audit.assignedToName[0]?.toUpperCase() ?? "?"}
            </div>
            <span className="text-xs text-muted-foreground">{audit.assignedToName}</span>
          </div>
        )}

        {audit.totalItems > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] text-muted-foreground">
              <span>
                {audit.checkedItems}/{audit.totalItems} items
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </button>

      {project && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-1.5 right-1.5 h-6 w-6"
          onClick={() => onShowTeam(project)}
        >
          <Users className="h-3.5 w-3.5" />
          <span className="sr-only">View team</span>
        </Button>
      )}
    </div>
  );
}

function KanbanColumn({
  column,
  audits,
  loading,
  projectsById,
  onShowTeam,
}: {
  column: (typeof COLUMNS)[number];
  audits: AuditListItem[];
  loading: boolean;
  projectsById: Map<string, Project>;
  onShowTeam: (project: Project) => void;
}) {
  const colorClass = COLUMN_COLORS[column.key] ?? "border-t-border";

  return (
    <div
      className={`flex flex-col bg-muted/30 rounded-lg border border-border border-t-2 ${colorClass} min-h-[400px] w-full`}
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <span className="text-sm font-medium">{column.label}</span>
        <Badge variant={BADGE_VARIANTS[column.key] ?? "outline"} className="text-xs">
          {loading ? "—" : audits.length}
        </Badge>
      </div>

      <div className="flex-1 p-2 space-y-2 overflow-y-auto">
        {loading ? (
          ["sk-1", "sk-2"].map((key) => (
            <div key={key} className="bg-card border border-border rounded-lg p-3 space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse" />
              <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
            </div>
          ))
        ) : audits.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 text-xs text-muted-foreground">
            <ClipboardCheck className="h-6 w-6 mb-1 opacity-30" />
            <span>No audits</span>
          </div>
        ) : (
          audits.map((audit) => (
            <AuditCard
              key={audit.id}
              audit={audit}
              project={audit.projectId ? projectsById.get(audit.projectId) : undefined}
              onShowTeam={onShowTeam}
            />
          ))
        )}
      </div>
    </div>
  );
}

const STATUS_FILTER_COLUMNS: Record<string, string[]> = {
  active: [...ACTIVE_AUDIT_STATUSES],
  complete: ["complete"],
  blocked: ["blocked"],
};

export function AuditKanban({
  statusFilter,
  searchQuery = "",
}: {
  statusFilter?: string;
  searchQuery?: string;
}) {
  const { data: session } = useSession();
  const { data: audits, isLoading } = useAudits();
  const { data: projects } = useAllProjects();
  const [createOpen, setCreateOpen] = useState(false);
  const [teamProject, setTeamProject] = useState<Project | null>(null);

  const projectsById = useMemo(() => new Map((projects ?? []).map((p) => [p.id, p])), [projects]);

  const role = session?.user?.role ?? "";
  const canCreate = role === "admin" || role === "compliance_manager";

  const allowedKeys = statusFilter ? STATUS_FILTER_COLUMNS[statusFilter] : undefined;
  const visibleColumns = allowedKeys ? COLUMNS.filter((c) => allowedKeys.includes(c.key)) : COLUMNS;

  const q = searchQuery.trim().toLowerCase();
  const filteredAudits = q
    ? (audits ?? []).filter(
        (a) =>
          a.projectName.toLowerCase().includes(q) ||
          (a.accountName ?? "").toLowerCase().includes(q) ||
          a.templateName.toLowerCase().includes(q) ||
          (a.assignedToName ?? "").toLowerCase().includes(q),
      )
    : (audits ?? []);

  const byStatus = COLUMNS.reduce<Record<string, AuditListItem[]>>((acc, col) => {
    acc[col.key] = filteredAudits.filter((a) => a.status === col.key);
    return acc;
  }, {});

  const colCount = visibleColumns.length;
  const gridClass =
    colCount === 1
      ? "grid-cols-1 max-w-sm"
      : colCount === 2
        ? "grid-cols-2"
        : colCount >= 5
          ? "grid-cols-2 md:grid-cols-5"
          : "grid-cols-2 md:grid-cols-4";

  return (
    <>
      <div className="space-y-4">
        {canCreate && (
          <div className="flex justify-end">
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Audit
            </Button>
          </div>
        )}

        <div className={`grid ${gridClass} gap-3`}>
          {visibleColumns.map((col) => (
            <KanbanColumn
              key={col.key}
              column={col}
              audits={byStatus[col.key] ?? []}
              loading={isLoading}
              projectsById={projectsById}
              onShowTeam={setTeamProject}
            />
          ))}
        </div>
      </div>

      <CreateAuditDialog open={createOpen} onOpenChange={setCreateOpen} />

      {teamProject && (
        <ProjectTeamDialog
          open
          onOpenChange={(open) => !open && setTeamProject(null)}
          project={teamProject}
        />
      )}
    </>
  );
}
